/**
 * Single transport for the whole app. Flip VITE_USE_MOCK=false once the Go API
 * on :8700 is up — nothing else in the app changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { mockRequest, ApiError } from '../mock/server'

const USE_MOCK = (import.meta.env.VITE_USE_MOCK ?? 'true') !== 'false'
const BASE = import.meta.env.VITE_API_BASE || '/api/v1'

// Module-level auth token so every request carries the Bearer automatically.
// AppProvider keeps this in sync with the persisted session (see setAuthToken).
let authToken = null
export function setAuthToken(t) { authToken = t || null }

// Global "your session is no longer accepted" hook. AppProvider registers one
// that clears the session and sends the user to the login screen.
//
// Needed because the API now really rejects: /admin/* used to serve requests
// with no token at all, so an expired token looked like a working session and a
// 401 was effectively unreachable. Access tokens are short-lived, so without
// this an admin who leaves a tab open comes back to red error boxes on every
// panel with no hint that logging in again is the fix.
let onUnauthorized = null
export function setOnUnauthorized(fn) { onUnauthorized = fn }

// Sesi berakhir karena orangnya KELUAR DARI THE MUSLIM di peramban ini
// (back-channel logout). Server menjawab 401 dengan `data.error` ini — lihat
// `auth.SessionEnded` di repo Go. Bedanya dengan 401 biasa disengaja: orang
// yang baru saja menekan Keluar di The Muslim tidak sedang ingin masuk lagi,
// jadi sesinya cukup dibersihkan dan ia jadi pengunjung biasa — tanpa modal
// masuk yang tiba-tiba terbuka. AppProvider mendaftarkan handler-nya.
export const SESSION_ENDED = 'session_ended'
let onSessionEnded = null
export function setOnSessionEnded(fn) { onSessionEnded = fn }

// Endpoints where 401 means "wrong credentials in THIS form", not "session
// dead". Both are submitted WITH a valid session attached — signing out of the
// password-change form because the old password was mistyped would be absurd.
const CREDENTIAL_CHECK = /^\/(auth\/|me\/password)/

const isSessionEnded = (code, data, sentToken) =>
  code === 401 && sentToken && data?.error === SESSION_ENDED

function noteUnauthorized(path, code, sentToken, data) {
  if (code !== 401 || !sentToken) return
  // session_ended berlaku di jalur MANA PUN, termasuk /auth/*: di sana 401
  // biasa memang bisa berarti "salah isi form", tapi kode ini hanya dikirim
  // untuk sesi yang sudah diakhiri The Muslim.
  if (data?.error === SESSION_ENDED) {
    // Bearer dibuang SEKARANG, bukan menunggu effect AppProvider: permintaan
    // berikutnya di putaran yang sama tidak boleh membawa token yang sama lagi.
    authToken = null
    onSessionEnded?.()
    return
  }
  if (CREDENTIAL_CHECK.test(path)) return
  onUnauthorized?.()
}

const qs = (query) => {
  const p = new URLSearchParams()
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return
    p.set(k, Array.isArray(v) ? v.join(',') : v)
  })
  const s = p.toString()
  return s ? `?${s}` : ''
}

export async function request(method, path, { query, body, token, retried } = {}) {
  if (USE_MOCK) {
    const res = await mockRequest(method, path, { query: query || {}, body })
    return res.data
  }
  const sent = token || authToken
  const res = await fetch(`${BASE}${path}${qs(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': localStorage.getItem('kx.locale') || 'id',
      ...(sent ? { Authorization: `Bearer ${sent}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  const json = await res.json().catch(() => ({ code: res.status, message: res.statusText, data: null }))
  if (!res.ok || json.code >= 400) {
    const code = json.code || res.status
    noteUnauthorized(path, code, !!sent, json.data)
    // GET yang ditolak HANYA karena token basinya diulang sekali sebagai tamu.
    // Halaman publik (beranda, katalog) jadi tetap terisi, alih-alih kotak
    // galat di tempat yang untuk pengunjung biasa selalu berhasil. Bukan
    // untuk POST/PUT/DELETE: tindakan yang dimaksudkan atas nama seseorang
    // tidak boleh diam-diam dijalankan sebagai tamu.
    if (method === 'GET' && !retried && isSessionEnded(code, json.data, !!sent)) {
      return request(method, path, { query, retried: true })
    }
    throw new ApiError(code, json.message || 'Request failed', json.data)
  }
  return json.data
}

export const api = {
  get: (path, query) => request('GET', path, { query }),
  post: (path, body) => request('POST', path, { body }),
  put: (path, body) => request('PUT', path, { body }),
  // PATCH dipakai untuk pembaruan sebagian, di mana field yang TIDAK dikirim
  // harus dibiarkan apa adanya (mis. PATCH /me/onboarding).
  patch: (path, body) => request('PATCH', path, { body }),
  del: (path) => request('DELETE', path),
  upload,
}

/** Upload a File via multipart/form-data. Returns the envelope's data ({key,url}). */
export async function upload(path, file, fields = {}) {
  if (USE_MOCK) {
    // Mock: no real storage — hand back a local object URL so previews work.
    return { key: `mock/${file.name}`, url: URL.createObjectURL(file) }
  }
  const fd = new FormData()
  fd.append('file', file)
  Object.entries(fields).forEach(([k, v]) => v != null && fd.append(k, v))
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Accept-Language': localStorage.getItem('kx.locale') || 'id',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      // NOTE: no Content-Type — the browser sets the multipart boundary itself.
    },
    body: fd,
  })
  const json = await res.json().catch(() => ({ code: res.status, message: res.statusText, data: null }))
  if (!res.ok || json.code >= 400) {
    const code = json.code || res.status
    noteUnauthorized(path, code, !!authToken, json.data)
    throw new ApiError(code, json.message || 'Upload gagal', json.data)
  }
  return json.data
}

export { ApiError }

/** GET with loading / error / refetch. `deps` re-runs the call. */
export function useApi(path, query, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const key = JSON.stringify({ path, query })
  const mounted = useRef(true)
  // The cleanup sets false, not true: every setState below is guarded on this,
  // and setting it to true made the guard a no-op, so navigating away mid-request
  // still wrote state into a dead component. Re-arming on mount matters because
  // StrictMode mounts, unmounts and remounts in dev — without it the flag would
  // stay false and the hook would never render data again.
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  // Nomor urut permintaan. Yang menang adalah permintaan TERBARU, bukan balasan
  // yang kebetulan tiba terakhir.
  //
  // Tanpa ini, dua permintaan ke jalur yang sama saling menimpa menurut siapa
  // yang lebih cepat sampai — dan itu bukan kasus pinggiran: StrictMode
  // menembak dua kali di dev, dan tiap `refetch()` sesudah menyimpan sesuatu
  // berlomba dengan permintaan lama yang masih di udara. API ini berbicara ke
  // Postgres lewat jaringan, jadi satu daftar bisa lewat beberapa detik dan
  // urutan tibanya benar-benar bisa terbalik.
  //
  // Gejalanya menyesatkan: layar kembali ke DATA LAMA sesudah penyimpanan yang
  // sukses, jadi yang terbaca adalah "simpanan saya tidak tersimpan" — padahal
  // databasenya sudah benar dan muat ulang membuktikannya.
  const seq = useRef(0)

  const run = useCallback(async () => {
    if (!path) return
    const ticket = ++seq.current
    const fresh = () => mounted.current && ticket === seq.current
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const data = await api.get(path, query)
      if (fresh()) setState({ data, loading: false, error: null })
    } catch (e) {
      if (fresh()) setState({ data: null, loading: false, error: e })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps])

  useEffect(() => { run() }, [run])
  return { ...state, refetch: run, setData: (d) => setState((s) => ({ ...s, data: typeof d === 'function' ? d(s.data) : d })) }
}

/** POST/PUT with pending + error state. */
export function useMutation(fn) {
  const [state, setState] = useState({ pending: false, error: null, data: null })
  const mutate = useCallback(async (...args) => {
    setState({ pending: true, error: null, data: null })
    try {
      const data = await fn(...args)
      setState({ pending: false, error: null, data })
      return data
    } catch (e) {
      setState({ pending: false, error: e, data: null })
      throw e
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn])
  return { ...state, mutate, reset: () => setState({ pending: false, error: null, data: null }) }
}

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}
